import { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { ApiHelper, USER_ROLE_ADMIN } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit/dist/hooks";
import { Refresh } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { StatefulControlledTextField } from "components/Core/StatefulControlledTextField";
import AgentAutoComplete, {
  dedupeBrokersByToken,
  normalizeAgentTokens,
} from "components/Marketing/AgentAutoComplete";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import DataGridContext from "core/DataGridContext";
import _ from "lodash";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { useSnackbar } from "notistack";
import { formatDate, formatDateLong, formatNumber, formatPrice, formatProducerName } from "utility/formatters";
import { v4 } from "uuid";

import { AddNoteIcon, ArrowDownIcon, BrokerIcon, CloseIcon, DeleteIcon, EditNoteIcon, SaveIcon } from "../Icons/Icons";
import DesktopDataGridSkeleton from "../shared/DataGrid/DesktopDataGridSkeleton";
import ResponsiveDataGrid from "../shared/DataGrid/ResponsiveDataGrid";
import { CropProfileEditDialog } from "./CropProfileEditDialog";
import SkeletonDataGrid from "./SkeletonDataGrid";

export default function TransactionImportWorkspace() {
  const { globalState, globalDispatch } = useContext(AppContext);
  const gridKey = "transaction-import-mobile-grid";
  const { getGridState, setGridState } = useContext(DataGridContext);
  const pageSizeOptions = [5, 10, 25, 50, 100];
  const defaultPageSize = 25;
  const { enqueueSnackbar } = useSnackbar();

  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();
  const { component: formStatusComponent, actions: formStatusActions } = useFormStatus();

  const RECORD_CODE_EXPIRATION = "X";
  const RECORD_CODE_EXERCISES_ASSIGNMENTS = "E";

  // ---

  const [brokers, setBrokers] = useState(null);
  const [isLoadingBrokers, setIsLoadingBrokers] = useState(false);
  const [isLoadingBatchApprove, setIsLoadingBatchApprove] = useState(false);

  const [editingCropProfile, setEditingCropProfile] = useState(null);
  const [isSavingNote, setIsSavingNote] = useState(false);

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });

  const loadBrokers = async () => {
    try {
      if (globalState?.agent_token) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {
          agent_token: globalState?.agent_token,
        };
        // This is agent function. Do not specify producer_token or you will get permissions error.
        const _apiConfig = {
          agent_token: globalState?.agent_token,
        };
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      } else if (globalState?.user_role === USER_ROLE_ADMIN) {
        setIsLoadingBrokers(true);

        const _brokersFilter = {};
        // This is ADMIN function. Do not specify producer_token or agent_token or you will get permissions error.
        const _apiConfig = {};
        const _brokers = await UsersApiHelper.users_search("AgencyAgent", _brokersFilter, _apiConfig);
        setBrokers(_brokers);
        setIsLoadingBrokers(false);
      }
    } catch (err) {
      setIsLoadingBrokers(false);
      setBrokers(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (globalState?.agent_token || globalState?.user_role === USER_ROLE_ADMIN) {
      setIsLoadingBrokers(true);
      loadBrokers();
    }
  }, [globalState?.agent_token, globalState?.user_role]);

  // ---

  const [cropProfiles, setCropProfiles] = useState(null);
  const [isLoadingCropProfiles, setIsLoadingCropProfiles] = useState(false);

  const buildCropProfilesDictionaryByProducer = (_cropProfiles) => {
    const _dictionary = {};
    // Populate: _dictionary[producer_token] should contain all bcp {year, commodity_name, crop_profile}
    _cropProfiles.forEach((cp) => {
      if (!_dictionary[cp.producer_token]) {
        _dictionary[cp.producer_token] = [];
      }

      const cropProfileDescription =
        cp.year + " - " + cp.commodity_name + (cp?.name && cp?.name !== cp?.commodity_name ? " - " + cp.name : "");

      const _entry = {
        year: cp.year,
        commodity_name: cp.commodity_name,
        crop_profile: cropProfileDescription,
      };

      _dictionary[cp.producer_token].push(_entry);
    });
    return _dictionary;
  };

  // Memoize crop_profile from cropProfiles
  const cropProfilesDictionary = useMemo(
    () => (cropProfiles?.length && buildCropProfilesDictionaryByProducer(cropProfiles)) || null,
    [cropProfiles]
  );

  const getCropProfileLabelsByProducerToken = useCallback(
    (producer_token) => {
      const _producerCropProfiles = cropProfilesDictionary?.[producer_token] || [];

      if (_producerCropProfiles?.length) {
        return (
          _producerCropProfiles
            // ?.filter(cp => cp?.commodity_name === commodity_name) // Strict equality check
            ?.map((cp) => cp?.crop_profile) || []
        );
      } else {
        return [];
      }
    },
    [cropProfilesDictionary]
  );

  const loadCropProfiles = async () => {
    try {
      setIsLoadingCropProfiles(true);

      const _filter = {
        agent_token: globalState?.agent_token,
        is_posted: filter?.filter_is_posted || false,
      };

      const promises = filter.filter_agent_token.map(async (agentToken) => {
        if (filter?.filter_agent_token && filter?.filter_agent_token.length) {
          _filter.filter_agent_token = agentToken;
        }

        return await UsersApiHelper.users_search("MarketTransactionHistoryCropProfile", _filter, globalState);
      });

      const allResults = await Promise.all(promises);

      setCropProfiles(allResults.flat());
      setIsLoadingCropProfiles(false);
    } catch (err) {
      setIsLoadingCropProfiles(false);
      setCropProfiles(null);
      handleError(err);
    }
  };

  // ---

  const [transactionHistory, setTransactionHistory] = useState([]);
  const [isLoadingTransactionHistory, setIsLoadingTransactionHistory] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);

  const transactionHistoryTradeDates = useMemo(
    () =>
      (transactionHistory?.length &&
        // Sort by date asc
        _.uniq(transactionHistory?.map((hist) => hist?.trade_date)).sort((a, b) => new Date(a) - new Date(b))) ||
      null,
    [transactionHistory]
  );

  const loadTransactionHistory = async () => {
    try {
      setIsLoadingTransactionHistory(true);
      setTransactionHistory([]);

      const _filter = {
        agent_token: globalState?.agent_token,
        is_posted: filter?.filter_is_posted || false,
      };

      const promises = filter.filter_agent_token.map(async (agentToken) => {
        if (filter?.filter_agent_token && filter?.filter_agent_token.length) {
          _filter.filter_agent_token = agentToken;
        }

        const apiClaims = {};
        if (globalState?.user_role !== USER_ROLE_ADMIN) {
          apiClaims.agent_token = globalState?.agent_token;
        }

        return await UsersApiHelper.selectMarketTransactionHistory(_filter, globalState);
      });

      const allResults = await Promise.all(promises);
      const allResultsFlat = allResults.flat();

      // Assign crop_profile = getDefaultCropProfile(params?.row)
      allResultsFlat.forEach((hist) => (hist.crop_profile = hist.crop_profile || getDefaultCropProfile(hist)));

      setTransactionHistory(allResultsFlat);
      setIsLoadingTransactionHistory(false);
      // Set data ready after a small delay to ensure state updates are processed
      setTimeout(() => setIsDataReady(true), 100);
    } catch (err) {
      setIsLoadingTransactionHistory(false);
      setIsDataReady(false);
      setTransactionHistory(null);
      handleError(err);
    }
  };

  const handleDispatchRowChange = useCallback(
    async (id, key, value) => {
      const _transactionHistory = [...transactionHistory];
      const _index = _transactionHistory.findIndex((hist) => hist.id === id);
      if (_index > -1) {
        _transactionHistory[_index][key] = value;
        setTransactionHistory(_transactionHistory);
      }
    },
    [transactionHistory, setTransactionHistory]
  );

  // ---

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    filter_agent_token: normalizeAgentTokens(
      globalState?.filter_agent_token?.length
        ? globalState.filter_agent_token
        : [globalState?.agent_token].filter(Boolean)
    ),
    filter_is_posted: false,
  });
  const selectedBroker = useMemo(() => {
    const tokens = new Set(normalizeAgentTokens(filter?.filter_agent_token));
    return dedupeBrokersByToken(
      brokers?.filter((broker) => tokens.has(String(broker?.agent_token ?? "").toLowerCase())) || []
    );
  }, [brokers, filter?.filter_agent_token, globalState?.agent_token]);

  const handleChangeAgentToken = async (agentTokens) => {
    dispatchFilter({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: normalizeAgentTokens(agentTokens.map((item) => item.agent_token)),
      },
    });
  };

  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "filter_agent_token",
        value: filter?.filter_agent_token, //array
      },
    });
  }, [filter?.filter_agent_token]);

  const filterHandleChangeState = (event, value) => {
    if (!event?.target) return;

    const eventValue = event.target.value;
    const eventName = event.target.name;

    value = value ?? eventValue;

    dispatchFilter({
      type: "update",
      payload: { key: eventName, value: value },
    });
    setTransactionHistory([]);
    setIsDataReady(false);
  };

  const handleRowApprove = useCallback(
    async (row) => {
      if (row?.crop_profile) {
        // Lookup crop profile from cropProfiles
        const _cropProfile =
          cropProfilesDictionary?.[row?.producer_token]?.find((cp) => cp?.crop_profile === row?.crop_profile) || null;

        let _cropProfileYear;
        let _cropProfileCommodityName;

        // If not configured, create one:
        if (_cropProfile) {
          _cropProfileYear = _cropProfile?.year;
          _cropProfileCommodityName = _cropProfile?.commodity_name;

          if (_cropProfileYear && _cropProfileCommodityName) {
            const _row = { ...row };
            _row.crop_profile_year = _cropProfileYear;
            _row.crop_profile_commodity_name = _cropProfileCommodityName;
            _row.is_posted = true;

            const api = new ApiHelper({
              baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
              config: {},
              sessionManager: _sessionManager,
              // No caching:
              _instance: null,
            });
            await api.callPost("broker/MarketTransactionHistory", _row, globalState);

            handleDispatchRowChange(row?.id, "is_posted", true);
          }
        } else {
          // // Parse
          // const _cropProfileParts = row?.crop_profile.split(" - ");
          // if (_cropProfileParts.length == 2) {
          //     _cropProfileYear = parseInt(_cropProfileParts[0]);
          //     _cropProfileCommodityName = _cropProfileParts[1];
          // }
          enqueueSnackbar("Crop profile not found. Please create a new one.", {
            preventDuplicate: true,
            variant: "error",
          });
          return;
        }
      }
    },
    [cropProfilesDictionary, handleDispatchRowChange]
  );

  const handleRowDelete = useCallback(
    async (row) => {
      confirmDialogActions.confirm({
        title: "Delete this entry?",
        prompt: "Are you sure you want to delete this entry?",
        promptDetail: null,
        callback: () => {
          _handleRowDeleteIntern(row);
        },
      });
    },
    [confirmDialogActions]
  );

  const _handleRowDeleteIntern = useCallback(
    async (row) => {
      const _row = { ...row };
      _row.crop_profile = null;
      _row.crop_profile_year = null;
      _row.crop_profile_commodity_name = null;
      _row.is_posted = false;

      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("broker/MarketTransactionHistory", _row, globalState);

      handleDispatchRowChange(row?.id, "crop_profile", null);
      handleDispatchRowChange(row?.id, "is_posted", false);
    },
    [handleDispatchRowChange]
  );

  useEffect(() => {
    if (globalState?.agent_token || (globalState?.user_role === USER_ROLE_ADMIN && filter?.filter_agent_token)) {
      setIsLoadingTransactionHistory(true);
      loadTransactionHistory();
    }
  }, [globalState?.agent_token, globalState?.user_role, filter]);

  useEffect(() => {
    if (globalState?.agent_token || (globalState?.user_role === USER_ROLE_ADMIN && filter?.filter_agent_token)) {
      setIsLoadingCropProfiles(true);
      loadCropProfiles();
    }
  }, [globalState?.agent_token, globalState?.user_role, filter]);

  // ---

  const getDefaultCropProfile = (row) => {
    if (row?.is_posted === null) {
      if (row?.crop_profile_year && row?.crop_profile_commodity_name) {
        const fullName =
          row?.crop_profile_year +
          " - " +
          row?.crop_profile_commodity_name +
          (row?.crop_profile_name !== row?.crop_profile_commodity_name ? " - " + row?.crop_profile_name : "");
        return fullName;
      } else {
        return null;
      }
    } else {
      return row?.crop_profile || null;
    }
  };

  const getCropProfileFromTransactionHistory = (row) => {
    const _cropProfile = {
      id: v4(),
      transaction_history_row_id: row?.id,
      producer_token: row?.producer_token,
      is_active: true,
    };
    if (row?.symbol_year && row?.commodity_name) {
      let _cropProfileYear = row?.symbol_year;
      if (row?.commodity_reference_month_term) {
        _cropProfileYear += row?.commodity_reference_month_term;
      }
      if (row?.commodity_reference_month && row?.symbol_month >= row?.commodity_reference_month) {
        _cropProfileYear++;
      }
      _cropProfile.year = _cropProfileYear;
      _cropProfile.commodity_name = row?.commodity_name;
    }

    return _cropProfile;
  };

  // ---
  const handleChange_CropProfile = async (_newCropProfile) => {
    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });
      await api.callPost("marketing/CropProfile", _newCropProfile, globalState);

      loadCropProfiles();

      let _cropProfile_name =
        _newCropProfile.year +
        " - " +
        _newCropProfile.commodity_name +
        (_newCropProfile?.name && _newCropProfile?.name !== _newCropProfile?.commodity_name
          ? " - " + _newCropProfile.name
          : "");
      handleDispatchRowChange(_newCropProfile.transaction_history_row_id, "crop_profile", _cropProfile_name);
    } catch (err) {
      handleError(err);
    }
  };

  const [groupedTransactionHistoryData, setGroupedTransactionHistoryData] = useState({});

  const noteInputRefs = useRef({});

  const getRef = (refKey) => {
    if (!noteInputRefs.current[refKey]) {
      noteInputRefs.current[refKey] = { current: null };
    }
    return noteInputRefs.current[refKey];
  };

  useEffect(() => {
    const groupData = () => {
      if (filter.filter_is_posted) transactionHistory?.sort((a, b) => new Date(b.trade_date) - new Date(a.trade_date));

      return (
        transactionHistory?.length &&
        transactionHistory?.reduce((acc, item) => {
          const key = `${item.batch_name?.substring(item.batch_name?.length - 13)}-${formatDateLong(item.trade_date)}`;
          if (!acc[key]) acc[key] = [];
          acc[key].push({
            ...item, // Spread the item
            showNote: false,
          });
          return acc;
        }, {})
      );
    };

    if (transactionHistory?.length) {
      setGroupedTransactionHistoryData(groupData());
      setIsDataReady(true);
    } else {
      setGroupedTransactionHistoryData({});
      setIsDataReady(false);
    }
  }, [transactionHistory, filter.filter_is_posted]);

  const handleBatchApprove = useCallback(
    async (batchKey) => {
      const batchTransactions = groupedTransactionHistoryData[batchKey];
      if (!batchTransactions || batchTransactions.length === 0) {
        enqueueSnackbar(`No transactions found for batch ${batchKey}.`, { variant: "warning" });
        return;
      }
      const failedTransactions = []; // Array to keep track of failed transactions
      for (const transaction of batchTransactions) {
        try {
          setIsLoadingBatchApprove(true);
          await handleRowApprove(transaction); // Attempt to approve the transaction
        } catch (error) {
          console.warn(`Error approving transaction with ID ${transaction.id}:`, error);
          // Log the failed transaction
          failedTransactions.push({
            transaction,
            error: error.message || "Unknown error",
          });
          // Notify the user and stop the process
          enqueueSnackbar(`Error processing transaction ${transaction.id} in batch ${batchKey}: ${error.message}`, {
            variant: "error",
          });
          setIsLoadingBatchApprove(false);
          break; // Stop further processing
        }
      }

      // Check if there were any failed transactions
      if (failedTransactions.length > 0) {
        console.error(`Failed transactions for batch ${batchKey}:`, failedTransactions);
        enqueueSnackbar(`Batch ${batchKey} approval halted. ${failedTransactions.length} transaction(s) failed.`, {
          variant: "error",
        });
        setIsLoadingBatchApprove(false);
      } else {
        enqueueSnackbar(`Batch ${batchKey} approved successfully.`, { variant: "success" });
        setIsLoadingBatchApprove(false);
        loadCropProfiles();
      }
    },
    [groupedTransactionHistoryData, handleRowApprove]
  );

  const getBuySellPosition = (position) => {
    if (String(position) === "1") return "Buy";
    if (String(position) === "2") return "Sell";
    return "Unknown";
  };

  const handleToggleNoteVisibility = (batchKey, rowIndex) => {
    setGroupedTransactionHistoryData((prevData) => {
      const updatedBatch = prevData[batchKey].map((row, index) =>
        index === rowIndex ? { ...row, showNote: !row.showNote } : row
      );
      return {
        ...prevData,
        [batchKey]: updatedBatch,
      };
    });

    setTimeout(() => {
      const refKey = `${batchKey}-${rowIndex}`;
      const inputElement = noteInputRefs.current[refKey]?.current;
      if (inputElement) {
        inputElement.focus();
        if (inputElement.setSelectionRange) {
          const length = inputElement.value ? inputElement.value.length : 0;
          inputElement.setSelectionRange(length, length);
        }
      }
    }, 100);
  };

  const handleNoteChange = async (
    e,
    batchKey,
    rowIndex,
    row,
    isShowNote = false,
    isUpdate = true,
    blankSaveFlag = false
  ) => {
    const { value } = e.target;
    const normalizedValue = value || "";
    const currentNote = row.note || "";

    if (filter?.filter_is_posted && (normalizedValue !== currentNote || blankSaveFlag) && isUpdate) {
      setIsSavingNote(true);
      try {
        const cleanedValue = normalizedValue
          .trim()
          .replace(/\s+/g, " ")
          .replace(/\n\s*\n/g, "\n")
          .replace(/\n\s+/g, "\n")
          .replace(/\s+\n/g, "\n");

        const updatedRow = {
          ...row,
          note: cleanedValue,
          is_active: row.is_active !== undefined ? row.is_active : true,
        };
        const isNoteDeletion = currentNote.trim() !== "" && cleanedValue.trim() === "";
        await updateTransactionHistory(updatedRow, isNoteDeletion);
      } catch (error) {
        handleError(error);
      } finally {
        setIsSavingNote(false);
      }
    }

    setGroupedTransactionHistoryData((prevData) => {
      const updatedBatch = prevData[batchKey].map((row, index) =>
        index === rowIndex ? { ...row, note: normalizedValue, showNote: !isShowNote } : row
      );
      return {
        ...prevData,
        [batchKey]: updatedBatch,
      };
    });
  };

  const handleNoteClearWithoutSave = (batchKey, rowIndex) => {
    setGroupedTransactionHistoryData((prevData) => {
      const updatedBatch = prevData[batchKey].map((row, index) => (index === rowIndex ? { ...row, note: "" } : row));
      return {
        ...prevData,
        [batchKey]: updatedBatch,
      };
    });
  };

  const updateTransactionHistory = async (row, isNoteDeletion = false) => {
    try {
      formStatusActions.setIsFormDirty(true, true);
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        // No caching:
        _instance: null,
      });

      await api.callPost("marketing/UserTransactionHistory", row, globalState);
      await loadTransactionHistory();
      formStatusActions.setIsFormDirty(false, false);

      const message = isNoteDeletion ? "Your note was deleted." : "Your note was saved.";
      enqueueSnackbar(message, { preventDuplicate: true, variant: "success" });
    } catch (err) {
      handleError(err);
    }
  };

  const initialState = getGridState()[gridKey] || {
    pageSize: defaultPageSize,
    sorting: null,
  };

  const [paginationModel, setPaginationModel] = useState({
    pageSize: initialState.pageSize || defaultPageSize,
    page: 0,
  });

  const [sortModel, setSortModel] = useState(
    initialState.sorting ? [initialState.sorting] : [] // Ensure an empty array if no sorting is defined
  );

  useEffect(() => {
    const savedState = getGridState()[gridKey];
    if (savedState) {
      setPaginationModel((prev) => ({
        ...prev,
        pageSize: savedState.pageSize || defaultPageSize,
      }));
      setSortModel(savedState.sorting ? [savedState.sorting] : []); // Validate sorting before setting
    }
  }, [getGridState, gridKey, defaultPageSize]);

  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    setGridState(gridKey, { pageSize: model.pageSize });
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setGridState(gridKey, { sorting: model[0] || null }); // Save only the first sorting object or null
  };

  // Define dynamic columns
  const columns = [
    {
      field: "producer",
      headerName: "Client",
      renderCell: (params) => {
        return formatProducerName(params.row);
      },
    },
    {
      field: "commodity",
      headerName: "Commodity",
      renderCell: (params) => params.row.commodity_name || params.row.bloomberg_root || "",
    },
    {
      field: "position",
      headerName: "Position",
      alignItems: "top",
      renderCell: (params) => {
        const { batchKey, rowIndex } = params.row;

        return (
          <Stack direction="column" spacing={1}>
            {params.row?.record_code === RECORD_CODE_EXERCISES_ASSIGNMENTS && (
              <span>Assigned - {formatDate(params.row?.entry_date)}</span>
            )}
            {params.row?.record_code === RECORD_CODE_EXPIRATION && (
              <span>Expired - {formatDate(params.row?.entry_date)}</span>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
              <span style={{ textTransform: "capitalize" }}>
                {`${getBuySellPosition(params.row.buy_sell_code)?.toLowerCase()} ${params.row.security_desc_line_1?.toLowerCase()}`}
              </span>
              {!params.row.showNote && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton onClick={() => handleToggleNoteVisibility(batchKey, rowIndex)} size="small">
                    {params.row.note ? <EditNoteIcon /> : <AddNoteIcon />}
                  </IconButton>
                </Box>
              )}
              {params.row.showNote && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <IconButton
                    onClick={async () => {
                      const refKey = `${batchKey}-${rowIndex}`;
                      const inputRef = getRef(refKey);
                      const currentValue = inputRef.current?.value || "";
                      await handleNoteChange(
                        { target: { value: currentValue } },
                        batchKey,
                        rowIndex,
                        params.row,
                        false,
                        true,
                        !params.row.note
                      );

                      handleToggleNoteVisibility(batchKey, rowIndex);
                    }}
                    size="small"
                    disabled={isLoadingTransactionHistory || isSavingNote}
                  >
                    {isSavingNote ? <CircularProgress size={16} /> : <SaveIcon />}
                  </IconButton>
                </Box>
              )}
            </Box>
          </Stack>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      align: "right",
      combine: true,
      fullWidth: (params) => params.row.showNote,
      renderCell: (params) => {
        const { batchKey, rowIndex } = params.row;
        const refKey = `${batchKey}-${rowIndex}`;

        return (
          <Box sx={{ width: params.row.showNote ? "100%" : "auto" }}>
            {params.row.showNote ? (
              <>
                <StatefulControlledTextField
                  label=""
                  placeholder="Notes"
                  variant="outlined"
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={4}
                  sx={{
                    width: "100%",
                    position: "relative",
                    "& .MuiInputBase-input": {
                      fontSize: "14px",
                    },
                  }}
                  inputProps={{
                    maxLength: 100,
                    onKeyDown: async (e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        const refKey = `${batchKey}-${rowIndex}`;
                        const inputRef = getRef(refKey);
                        const currentValue = inputRef.current?.value || "";
                        await handleNoteChange(
                          { target: { value: currentValue } },
                          batchKey,
                          rowIndex,
                          params.row,
                          false,
                          true
                        );
                        handleToggleNoteVisibility(batchKey, rowIndex);
                      }
                    },
                  }}
                  name={`note-input-${batchKey}-${rowIndex}`}
                  defaultValue={params.row.note || ""}
                  InputLabelProps={{ shrink: true }}
                  disabled={isLoadingTransactionHistory || isSavingNote}
                  endAdornment={
                    params.row.note && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => handleNoteClearWithoutSave(batchKey, rowIndex, params.row)}
                          onMouseDown={(event) => event.preventDefault()}
                          edge="end"
                          size="small"
                          sx={{
                            padding: "2px",
                            position: "absolute",
                            top: "12px",
                            right: "8px",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: "16px" }} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                  inputRef={getRef(refKey)}
                  fullWidth
                />
              </>
            ) : (
              params.row.note && (
                <Box
                  sx={{
                    fontSize: "14px",
                    cursor: "pointer",
                    width: "auto",
                    overflow: "hidden",
                    position: "relative",
                    paddingLeft: "0px",
                    marginLeft: "0px",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                    "& .note-label": {
                      color: "text.secondary",
                      position: "static",
                      marginRight: "8px",
                      paddingTop: "2px",
                    },
                    "& .note-content": {
                      color: "text.primary",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      lineHeight: "1.4em",
                      maxHeight: "5.6em",

                      "&:hover": {
                        WebkitLineClamp: "unset",
                        maxHeight: "none",
                      },
                    },
                  }}
                  onClick={() => handleToggleNoteVisibility(batchKey, rowIndex)}
                  title={params.row.note}
                >
                  <span className="note-label">Note:</span>
                  <span className="note-content">{params.row.note}</span>
                </Box>
              )
            )}
          </Box>
        );
      },
    },
    {
      field: "quantity",
      headerName: "Qty",
      align: "right",
      renderCell: (params) => formatNumber(params.row.quantity * params.row.multiplication_factor || 0, 0),
    },
    {
      field: "price",
      headerName: "Price",
      align: "right",
      renderCell: (params) => formatPrice(params.row.trade_price, 4),
    },
    {
      field: "trace",
      headerName: "Trace",
      renderCell: (params) => params.row.tracer_number,
    },
    {
      field: "crop_profile",
      headerName: "Crop Profile",
      renderCell: (params) => (
        <FormControl fullWidth size="small">
          <TextField
            select
            value={params.row.crop_profile || ""}
            onChange={(e) => handleDispatchRowChange(params.row.id, "crop_profile", e.target.value)}
            disabled={params.row?.is_posted !== null}
          >
            {getCropProfileLabelsByProducerToken(params.row.producer_token, params.row.commodity_name).map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>
      ),
      renderCard: (params) => params.row.crop_profile,
    },
    {
      field: "actions",
      headerName: "Actions",
      type: "actions",
      headerAlign: "right",
      align: "right",
      width: 150,
      sortable: false,
      getActions: (params) => {
        const isDisabled = params.row?.is_posted !== null;
        return [
          <GridActionsCellItem
            key="delete"
            label="Delete"
            size="small"
            onClick={() => (isDisabled ? null : handleRowDelete(params.row))}
            icon={<DeleteIcon sx={{ color: isDisabled ? "text.disabled" : "error.main" }} />}
          />,
        ];
      },
    },
  ];
  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title="Transactions"
        category={{
          title: "Broker",
          icon: <BrokerIcon />,
        }}
      />
      <Stack
        className="headerWithMenuButton"
        sx={{
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: "480px" },
            maxWidth: "100%",
            overflow: "hidden",
            "& .MuiFormControl-root": {
              width: "100%",
            },
          }}
        >
          <AgentAutoComplete brokers={brokers} selectedBroker={selectedBroker} onChange={handleChangeAgentToken} />
        </Box>

        <Box
          sx={{ display: "flex", flexDirection: "row", gap: "1rem", alignSelf: "start", paddingTop: { md: "1.75rem" } }}
        >
          <PopupState variant="popover" popupId="demo-popup-menu">
            {(popupState) => (
              <>
                <Button variant="outlined" {...bindTrigger(popupState)}>
                  {filter?.filter_is_posted ? "Posted" : "Unposted"}
                  <ArrowDownIcon sx={{ color: "inherit", marginLeft: 1 }} />
                </Button>
                <Menu {...bindMenu(popupState)}>
                  <MenuItem
                    onClick={() => {
                      filterHandleChangeState({ target: { name: "filter_is_posted", value: false } });
                      popupState.close();
                    }}
                  >
                    Unposted
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      filterHandleChangeState({ target: { name: "filter_is_posted", value: true } });
                      popupState.close();
                    }}
                  >
                    Posted
                  </MenuItem>
                </Menu>
                {formStatusComponent}
              </>
            )}
          </PopupState>

          <IconButton
            className="hiddenButton"
            size="large"
            onClick={() => {
              loadTransactionHistory();
              loadCropProfiles();
            }}
          >
            <Refresh size="large" />
          </IconButton>

          {(isLoadingBrokers ||
            isLoadingCropProfiles ||
            isLoadingTransactionHistory ||
            isLoadingBatchApprove ||
            (!isDataReady && Boolean(transactionHistory?.length))) && <CircularProgress />}
        </Box>
      </Stack>

      {isLoadingTransactionHistory || !isDataReady ? (
        isMobile ? (
          <SkeletonDataGrid columns={columns} rowsCount={1} />
        ) : (
          <DesktopDataGridSkeleton columns={columns} rowsCount={5} />
        )
      ) : (
        Boolean(transactionHistoryTradeDates?.length) && (
          <>
            <TableContainer>
              {Object.keys(groupedTransactionHistoryData).map((batchKey) => {
                const [, tradeDate] = batchKey.split("-");
                const groupedTransactionHistoryRows = groupedTransactionHistoryData[batchKey].map((row, rowIndex) => ({
                  ...row,
                  id: `${row.id || "unknown"}-${batchKey}-${rowIndex}`, // Create unique ID to prevent duplicate key warnings
                  rowIndex,
                  batchKey,
                }));
                return (
                  <div key={batchKey}>
                    <Stack direction="row" flex sx={{ justifyContent: "space-between", alignItems: "center" }}>
                      <Typography
                        className="trade-date"
                        variant="h5"
                        gutterBottom
                        style={{ marginTop: "30px" }}
                        data-testid={`trade-date-typography-${batchKey}`}
                      >
                        {tradeDate}
                      </Typography>

                      {!filter.filter_is_posted && (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleBatchApprove(batchKey)}
                          style={{ marginLeft: "10px" }}
                        >
                          Approve
                        </Button>
                      )}
                    </Stack>
                    {isMobile ? (
                      <ResponsiveDataGrid
                        rows={groupedTransactionHistoryRows}
                        columns={columns}
                        loading={!isSavingNote ? isLoadingTransactionHistory : false}
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        pageSizeOptions={pageSizeOptions}
                      />
                    ) : (
                      <Table
                        data-testid="transaction-import-table"
                        stickyHeader
                        aria-label="sticky table"
                        className="marketing-table transaction-import-workspace-table transaction-data-table"
                      >
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Client</TableCell>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Commodity</TableCell>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Position</TableCell>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Qty</TableCell>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Price</TableCell>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Trace</TableCell>
                            <TableCell sx={{ fontWeight: "bold", backgroundColor: "grey.100" }}>Crop Profile</TableCell>
                            <TableCell
                              sx={{
                                textAlign: "right",
                                fontWeight: "bold",
                                backgroundColor: "grey.100",
                              }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {groupedTransactionHistoryData[batchKey].map((row, index) => {
                            const refKey = `${batchKey}-${index}`;

                            return (
                              <TableRow key={index}>
                                <TableCell
                                  sx={{
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: row.note || row.showNote ? "18px" : "0px",
                                    "&.MuiTableCell-root": {
                                      paddingTop: row.note || row.showNote ? "18px" : "0px",
                                    },
                                  }}
                                >
                                  <Box sx={{ pb: 1 }}>{formatProducerName(row)}</Box>
                                  <Box>{row.account_name}</Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    "&.MuiTableCell-root": {
                                      paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    },
                                  }}
                                >
                                  {row?.commodity_name || row?.bloomberg_root || ""}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    maxWidth: "300px",
                                    width: "300px",
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: row.showNote ? "18px" : "8px",
                                    "&.MuiTableCell-root": {
                                      paddingTop: row.showNote ? "18px" : "8px",
                                    },
                                  }}
                                >
                                  <Stack direction="column" spacing={1}>
                                    {row?.record_code === RECORD_CODE_EXERCISES_ASSIGNMENTS && (
                                      <span>Assigned - {formatDate(row?.entry_date)}</span>
                                    )}
                                    {row?.record_code === RECORD_CODE_EXPIRATION && (
                                      <span>Expired - {formatDate(row?.entry_date)}</span>
                                    )}
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      <span style={{ textTransform: "capitalize" }}>
                                        {`${getBuySellPosition(row.buy_sell_code)?.toLowerCase()} ${row.security_desc_line_1?.toLowerCase()}`}
                                      </span>
                                      {!row.showNote && (
                                        <IconButton
                                          onClick={() => handleToggleNoteVisibility(batchKey, index)}
                                          size="small"
                                          disabled={isLoadingTransactionHistory || isSavingNote}
                                          sx={{
                                            "&:disabled": {
                                              color: "text.disabled",
                                              opacity: 0.6,
                                            },
                                          }}
                                        >
                                          {row.note ? <EditNoteIcon /> : <AddNoteIcon />}
                                        </IconButton>
                                      )}
                                      {row.showNote && (
                                        <IconButton
                                          onClick={async () => {
                                            const refKey = `${batchKey}-${index}`;
                                            const inputRef = getRef(refKey);
                                            const currentValue = inputRef.current?.value || "";
                                            await handleNoteChange(
                                              { target: { value: currentValue } },
                                              batchKey,
                                              index,
                                              row,
                                              false,
                                              true
                                            );
                                            handleToggleNoteVisibility(batchKey, index);
                                          }}
                                          size="small"
                                          disabled={isLoadingTransactionHistory || isSavingNote}
                                          sx={{
                                            "&:disabled": {
                                              color: "text.disabled",
                                              opacity: 0.6,
                                            },
                                          }}
                                        >
                                          <SaveIcon />
                                        </IconButton>
                                      )}
                                    </Box>
                                  </Stack>
                                  <Box sx={{ marginTop: "12px" }}>
                                    {row.showNote ? (
                                      <>
                                        <StatefulControlledTextField
                                          label=""
                                          placeholder="Notes"
                                          variant="outlined"
                                          size="small"
                                          multiline
                                          minRows={1}
                                          maxRows={4}
                                          sx={{
                                            maxWidth: "280px",
                                            position: "relative",
                                            "& .MuiInputBase-input": {
                                              fontSize: "14px",
                                            },
                                          }}
                                          inputProps={{
                                            maxLength: 100,
                                            onKeyDown: async (e) => {
                                              if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                const refKey = `${batchKey}-${index}`;
                                                const inputRef = getRef(refKey);
                                                const currentValue = inputRef.current?.value || "";
                                                await handleNoteChange(
                                                  { target: { value: currentValue } },
                                                  batchKey,
                                                  index,
                                                  row,
                                                  false,
                                                  true
                                                );
                                                handleToggleNoteVisibility(batchKey, index);
                                              }
                                            },
                                          }}
                                          name={`note-input-${batchKey}-${index}`}
                                          defaultValue={row.note || ""}
                                          InputLabelProps={{ shrink: true }}
                                          disabled={isLoadingTransactionHistory || isSavingNote}
                                          endAdornment={
                                            row.note && (
                                              <InputAdornment position="end">
                                                <IconButton
                                                  onClick={() => handleNoteClearWithoutSave(batchKey, index, row)}
                                                  onMouseDown={(event) => event.preventDefault()}
                                                  edge="end"
                                                  size="small"
                                                  sx={{
                                                    padding: "2px",
                                                    position: "absolute",
                                                    top: "12px",
                                                    right: "8px",
                                                    "&:hover": {
                                                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                                                    },
                                                  }}
                                                >
                                                  <CloseIcon sx={{ fontSize: "20px" }} />
                                                </IconButton>
                                              </InputAdornment>
                                            )
                                          }
                                          inputRef={getRef(refKey)}
                                          fullWidth
                                        />
                                      </>
                                    ) : (
                                      row.note && (
                                        <Box
                                          sx={{
                                            fontSize: "14px",
                                            cursor: "pointer",
                                            maxWidth: "280px",
                                            overflow: "hidden",
                                            position: "relative",
                                            paddingLeft: "40px",
                                            "&:hover": {
                                              textDecoration: "underline",
                                            },
                                            "& .note-label": {
                                              color: "text.secondary",
                                              position: "absolute",
                                              left: 0,
                                              top: 0,
                                              paddingTop: "2px",
                                            },
                                            "& .note-content": {
                                              color: "text.primary",
                                              overflow: "hidden",
                                              display: "-webkit-box",
                                              WebkitLineClamp: 4,
                                              WebkitBoxOrient: "vertical",
                                              lineHeight: "1.4em",
                                              maxHeight: "5.6em",

                                              "&:hover": {
                                                WebkitLineClamp: "unset",
                                                maxHeight: "none",
                                              },
                                            },
                                          }}
                                          onClick={() => handleToggleNoteVisibility(batchKey, index)}
                                          title={row.note}
                                        >
                                          <span className="note-label">Note:</span>
                                          <span className="note-content">{row.note}</span>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    "&.MuiTableCell-root": {
                                      paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    },
                                  }}
                                >
                                  {formatNumber(row?.quantity * row?.multiplication_factor || 0, 0)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    "&.MuiTableCell-root": {
                                      paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    },
                                  }}
                                >
                                  {formatPrice(row.trade_price, 4)}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    "&.MuiTableCell-root": {
                                      paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "18px" : "0px",
                                    },
                                  }}
                                >
                                  {row.tracer_number}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    p: "0px",
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                    paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "8px" : "6px",
                                    "&.MuiTableCell-root": {
                                      p: "0px",
                                      paddingTop: (row.note && row.note.trim() !== "") || row.showNote ? "8px" : "6px",
                                    },
                                  }}
                                >
                                  <Box sx={{ mb: 1, width: "100%", py: "10px" }}>
                                    <FormControl fullWidth>
                                      <Autocomplete
                                        name="crop_profile"
                                        options={
                                          getCropProfileLabelsByProducerToken(
                                            row?.producer_token,
                                            row?.commodity_name
                                          ) || []
                                        }
                                        multiple={false}
                                        value={row?.crop_profile || null}
                                        disabled={row?.is_posted !== null}
                                        onChange={(event, value) => {
                                          handleDispatchRowChange(row.id, "crop_profile", value);
                                        }}
                                        autoSelect
                                        autoHighlight
                                        renderInput={(params) => (
                                          <TextField
                                            name="crop_profile"
                                            {...params}
                                            required
                                            onChange={null}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        )}
                                        renderOption={(props, option) => {
                                          const { key, ...optionProps } = props;
                                          return (
                                            <li key={key} {...optionProps}>
                                              <Box>{option}</Box>
                                            </li>
                                          );
                                        }}
                                        PaperComponent={({ children }) => {
                                          return (
                                            <Paper>
                                              {children}
                                              <Button
                                                color="primary"
                                                variant="text"
                                                fullWidth
                                                sx={{ borderRadius: 0 }}
                                                onMouseDown={() => {
                                                  setEditingCropProfile(getCropProfileFromTransactionHistory(row));
                                                }}
                                              >
                                                + Add New
                                              </Button>
                                            </Paper>
                                          );
                                        }}
                                      />
                                    </FormControl>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    textAlign: "right",
                                    verticalAlign: row.note || row.showNote ? "top" : "middle",
                                  }}
                                >
                                  <IconButton
                                    size="large"
                                    disabled={row?.is_posted !== null}
                                    onClick={() => handleRowDelete(row)}
                                    sx={{
                                      color: "error.main",
                                      "& .MuiSvgIcon-root": { color: "error.main" },
                                      "&:disabled": {
                                        opacity: 0.6,
                                        color: "text.disabled",
                                        "& .MuiSvgIcon-root": { color: "text.disabled" },
                                      },
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    )}
                    <Divider />
                  </div>
                );
              })}
            </TableContainer>
          </>
        )
      )}

      <Box className="container transaction-import-workspace-container">
        {!isLoadingTransactionHistory && isDataReady && !transactionHistoryTradeDates?.length && (
          <EmptyCard
            text="No transactions found"
            secondaryText={
              filter?.filter_is_posted ? "No posted transactions were found." : "No pending transactions were found."
            }
          />
        )}
      </Box>

      <Dialog open={Boolean(editingCropProfile)}>
        <CropProfileEditDialog
          cropProfile={editingCropProfile}
          onClose={() => setEditingCropProfile(null)}
          onChange={handleChange_CropProfile}
        />
      </Dialog>

      {confirmDialogComponent}
    </>
  );
}
